import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { AsyncPaginate } from 'react-select-async-paginate';
import { loadLawftIdOptions } from 'hooks/queries/useLawftIdDropdown';
import '../shared/AsyncSelect.scss';

function MultiValueLabelWithTitle({ innerProps, children, data }) {
  return (
    <div {...innerProps} title={data?.label || (typeof children === 'string' ? children : undefined)}>
      {children}
    </div>
  );
}

function CheckboxOption({ innerRef, innerProps, isSelected, isFocused, children }) {
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={[
        'async-select-custom__option',
        isFocused ? 'async-select-custom__option--is-focused' : '',
        isSelected ? 'async-select-custom__option--is-selected' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
    >
      <span
        className={`lawft-id-select-checkbox${isSelected ? ' lawft-id-select-checkbox--checked' : ''}`}
        style={{ flexShrink: 0 }}
        aria-hidden="true"
      />
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{children}</span>
    </div>
  );
}

/**
 * Async-paginated multi-select for Lawft IDs (RDS list API).
 */
function LawftIdSelect({
  value = [],
  onChange,
  placeholder = 'Select Lawft ID',
  isClearable = true,
  isDisabled = false,
  className = '',
  pageSize = 10,
  status = 'ACTIVE',
  filters = {},
  practice_area = '',
  excludeLawftIds = [],
  styles = {},
  name,
  id,
  onBlur,
  onFocus,
  onOptionsLoaded,
  menuPortalTarget = null,
  menuPosition = 'absolute',
  ...rest
}) {
  const handleLoadOptions = useMemo(
    () => async (search, loadedOptions, additional) => {
      const result = await loadLawftIdOptions(search, loadedOptions, additional, {
        pageSize,
        status,
        filters,
        practice_area,
        excludeLawftIds,
      });
      onOptionsLoaded?.(result?.options || []);
      return result;
    },
    [pageSize, status, filters, practice_area, excludeLawftIds, onOptionsLoaded]
  );

  const mergedStyles = useMemo(
    () => ({
      control: (base) => ({
        ...base,
        minHeight: '38px',
      }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      ...styles,
    }),
    [styles]
  );

  const components = useMemo(() => ({ Option: CheckboxOption, MultiValueLabel: MultiValueLabelWithTitle }), []);

  const cacheUniqs = useMemo(
    () => [status, JSON.stringify(filters), JSON.stringify(practice_area), JSON.stringify(excludeLawftIds)],
    [status, filters, practice_area, excludeLawftIds]
  );

  return (
    <AsyncPaginate
      value={value}
      loadOptions={handleLoadOptions}
      onChange={onChange}
      getOptionLabel={(option) => option?.label || option?.lawft_id_name}
      getOptionValue={(option) => option?.value || option?.lawft_id}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isSearchable
      isMulti
      hideSelectedOptions={false}
      closeMenuOnSelect={false}
      defaultOptions
      className={`lawft-id-select ${className}`.trim()}
      classNamePrefix="async-select-custom"
      styles={mergedStyles}
      components={components}
      additional={{ page: 1 }}
      cacheUniqs={cacheUniqs}
      debounceTimeout={300}
      name={name}
      id={id}
      onBlur={onBlur}
      onFocus={onFocus}
      loadingMessage={() => 'Loading Lawft IDs...'}
      noOptionsMessage={({ inputValue }) => (inputValue ? `No Lawft IDs found for "${inputValue}"` : 'No Lawft IDs available')}
      menuPortalTarget={menuPortalTarget ?? (typeof document !== 'undefined' ? document.body : null)}
      menuPosition={menuPortalTarget ? menuPosition : 'fixed'}
      {...rest}
    />
  );
}

LawftIdSelect.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  className: PropTypes.string,
  pageSize: PropTypes.number,
  status: PropTypes.string,
  filters: PropTypes.object,
  practice_area: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  excludeLawftIds: PropTypes.array,
  styles: PropTypes.object,
  name: PropTypes.string,
  id: PropTypes.string,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onOptionsLoaded: PropTypes.func,
};

export default LawftIdSelect;
